import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { BookOpen, FileText, CheckCircle, ClipboardList, RefreshCw, Mic, Award, Download, Play } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTENT_ICONS = {
  lesson: BookOpen,
  task: FileText,
  correction: CheckCircle,
  evaluation: ClipboardList,
  reinforcement: RefreshCw
};

const CONTENT_COLORS = {
  lesson: 'bg-blue-100 text-blue-700',
  task: 'bg-amber-100 text-amber-700',
  correction: 'bg-emerald-100 text-emerald-700',
  evaluation: 'bg-red-100 text-red-700',
  reinforcement: 'bg-purple-100 text-purple-700'
};

export default function StudentProgressDialog({ open, onClose, studentId, classId }) {
  const { token } = useAuth();
  const { t } = useLanguage();
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchProgress = useCallback(async () => {
    if (!studentId || !classId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/content/student-progress/${classId}/${studentId}`, headers);
      setData(res.data);
    } catch (err) {
      console.error('Error fetching student progress:', err);
    } finally {
      setLoading(false);
    }
  }, [studentId, classId, token]);

  useEffect(() => {
    if (open) {
      fetchProgress();
    }
  }, [open, fetchProgress]);

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('es-ES', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const groupContentByType = (content) => {
    const groups = {};
    content?.forEach(item => {
      if (!groups[item.content_type]) {
        groups[item.content_type] = [];
      }
      groups[item.content_type].push(item);
    });
    return groups;
  };

  if (!data && loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const student = data?.student;
  const contentGroups = groupContentByType(data?.content || []);
  const recordings = data?.recordings || [];
  const feedbackReceived = data?.feedback_received || [];
  const gamification = data?.gamification;
  const badges = data?.badges || [];

  const initials = `${(student?.first_name || '')[0] || ''}${(student?.last_name || '')[0] || ''}`.toUpperCase();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Montserrat' }}>Mi Progreso - Control del Estudiante</DialogTitle>
        </DialogHeader>

        {student && (
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl mb-4">
            <Avatar className="h-16 w-16 border-2 border-blue-200">
              <AvatarImage src={student.profile_image ? `${API}/files/${student.profile_image}` : ''} />
              <AvatarFallback className="bg-blue-500 text-white text-xl font-bold">{initials}</AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-bold text-slate-800">{student.first_name} {student.last_name}</h3>
              <p className="text-sm text-slate-500">@{student.username} • {student.email}</p>
              {gamification && (
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="bg-amber-100 text-amber-700">Nivel {gamification.level || 1}</Badge>
                  <Badge variant="outline">{gamification.xp || 0} XP</Badge>
                </div>
              )}
            </div>
          </div>
        )}

        <Tabs defaultValue="content" className="w-full">
          <TabsList className="w-full grid grid-cols-4 mb-4">
            <TabsTrigger value="content">Contenido</TabsTrigger>
            <TabsTrigger value="recordings">Grabaciones</TabsTrigger>
            <TabsTrigger value="badges">Insignias</TabsTrigger>
            <TabsTrigger value="feedback">Retroalimentación</TabsTrigger>
          </TabsList>

          {/* Content Tab */}
          <TabsContent value="content" className="space-y-4">
            {Object.keys(contentGroups).length === 0 ? (
              <p className="text-center text-slate-400 py-8">No hay contenido asignado</p>
            ) : (
              Object.entries(contentGroups).map(([type, items]) => {
                const Icon = CONTENT_ICONS[type] || FileText;
                const colorClass = CONTENT_COLORS[type] || 'bg-slate-100 text-slate-700';
                return (
                  <div key={type} className="border rounded-xl p-4">
                    <h4 className="font-bold text-slate-700 flex items-center gap-2 mb-3 capitalize">
                      <Icon className="w-5 h-5" />
                      {t(type) || type}s ({items.length})
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {items.map(item => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={`text-xs ${colorClass}`}>{t(type)}</Badge>
                              <span className="text-xs text-slate-400">{formatDate(item.created_at)}</span>
                              {item.student_submission && (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Entregado</Badge>
                              )}
                              {item.student_score && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">
                                  Nota: {item.student_score.score}
                                </Badge>
                              )}
                            </div>
                          </div>
                          {item.files?.length > 0 && (
                            <a
                              href={`${API}/files/${item.files[0].filename}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-700"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </TabsContent>

          {/* Recordings Tab */}
          <TabsContent value="recordings" className="space-y-4">
            <div className="border rounded-xl p-4">
              <h4 className="font-bold text-slate-700 flex items-center gap-2 mb-3">
                <Mic className="w-5 h-5 text-cyan-500" />
                Grabaciones del Estudiante ({recordings.length})
              </h4>
              {recordings.length === 0 ? (
                <p className="text-center text-slate-400 py-4">No hay grabaciones</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {recordings.map(rec => (
                    <div key={rec.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">{rec.unit_name}</Badge>
                        <span className="text-xs text-slate-400">{formatDate(rec.created_at)}</span>
                      </div>
                      <audio src={`${API}/files/${rec.filename}`} controls className="w-full" preload="metadata" type="audio/webm" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="badges" className="space-y-4">
            <div className="border rounded-xl p-4">
              <h4 className="font-bold text-slate-700 flex items-center gap-2 mb-3">
                <Award className="w-5 h-5 text-amber-500" />
                Insignias Obtenidas ({badges.length})
              </h4>
              {badges.length === 0 ? (
                <p className="text-center text-slate-400 py-4">No ha obtenido insignias aún</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {badges.map((badge, i) => (
                    <div key={badge.id || badge.badge_id || badge.name || `b-${i}`} className="p-3 bg-amber-50 rounded-lg text-center border border-amber-200">
                      <Award className="w-8 h-8 text-amber-500 mx-auto mb-1" />
                      <p className="text-sm font-medium text-amber-700">{badge.name || badge.badge_name}</p>
                      <p className="text-xs text-slate-400">{badge.earned_at ? formatDate(badge.earned_at) : ''}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback" className="space-y-4">
            <div className="border rounded-xl p-4">
              <h4 className="font-bold text-slate-700 flex items-center gap-2 mb-3">
                <Play className="w-5 h-5 text-emerald-500" />
                Retroalimentación Recibida ({feedbackReceived.length})
              </h4>
              {feedbackReceived.length === 0 ? (
                <p className="text-center text-slate-400 py-4">No ha recibido retroalimentación</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {feedbackReceived.map(fb => (
                    <div key={fb.id} className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">De: {fb.teacher_name}</Badge>
                        <span className="text-xs text-slate-400">{formatDate(fb.created_at)}</span>
                      </div>
                      {fb.message && <p className="text-sm text-slate-600 mb-2">{fb.message}</p>}
                      <audio src={`${API}/files/${fb.filename}`} controls className="w-full" preload="metadata" type="audio/webm" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
